import React from "react";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/layout/AppShell";
import { ROLE_LABELS, MODULE_LABELS, hasModule } from "../lib/rbac";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { User, Mail, Shield, Building2, CheckCircle2, XCircle, LayoutGrid } from "lucide-react";

function Row({ icon: Icon, label, value, testid }) {
    return (
        <div className="flex items-start gap-4 py-4 border-b last:border-b-0">
            <div className="w-9 h-9 rounded-md bg-muted/60 flex items-center justify-center text-muted-foreground shrink-0">
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                    {label}
                </div>
                <div
                    className="text-sm font-medium text-foreground break-words"
                    data-testid={testid}
                >
                    {value || "—"}
                </div>
            </div>
        </div>
    );
}

export default function Profile() {
    const { user } = useAuth();

    return (
        <AppShell title="Profil" subtitle="Hesap Bilgileri">
            <div className="max-w-2xl" data-testid="profile-page">
                <Card>
                    <CardHeader className="flex flex-row items-center gap-4 pb-4">
                        <div
                            className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-semibold"
                            data-testid="profile-avatar"
                        >
                            {user?.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                            <CardTitle className="text-lg">{user?.name || "—"}</CardTitle>
                            <div className="text-xs text-muted-foreground mt-1">
                                {ROLE_LABELS[user?.role] || user?.role || "—"}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-2">
                        <Row icon={User} label="Ad Soyad" value={user?.name} testid="profile-name" />
                        <Row icon={Mail} label="E-posta" value={user?.email} testid="profile-email" />
                        <Row icon={Shield} label="Rol" value={ROLE_LABELS[user?.role] || user?.role} testid="profile-role" />
                        <Row icon={Building2} label="Departman" value={user?.department} testid="profile-department" />

                        {/* Module access — read-only */}
                        <div className="flex items-start gap-4 py-4">
                            <div className="w-9 h-9 rounded-md bg-muted/60 flex items-center justify-center text-muted-foreground shrink-0">
                                <LayoutGrid className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                                    Yetkili Olduğunuz Modüller
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="profile-modules">
                                    {Object.entries(MODULE_LABELS).map(([key, label]) => {
                                        const on = hasModule(user, key);
                                        return (
                                            <div
                                                key={key}
                                                data-testid={`profile-module-${key}`}
                                                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border ${
                                                    on
                                                        ? "bg-primary/10 text-primary border-primary/30"
                                                        : "bg-muted/40 text-muted-foreground border-border"
                                                }`}
                                            >
                                                {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                                                {label}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <p className="text-xs text-muted-foreground mt-4">
                    Bu ekran salt okunur görüntülemedir. Rol, departman veya modül yetkilerinizin
                    değiştirilmesi için sistem yöneticinize başvurun.
                </p>
            </div>
        </AppShell>
    );
}
